import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { backendUrl } from '../utils/AxiosConfiguration';
import Loading from '../pages/Loading';
import { ClassicTemplate } from './ClassicTemplate';
import { ClassicGone, ClassicLanding, ClassicNotFound } from './ClassicErrorViews';
import { TemplateErrorBoundary } from './TemplateErrorBoundary';
import { reportStatEvent } from './reportStatEvent';
import {
  NotFoundPayload,
  SUPPORTED_CONTRACT_VERSIONS,
  TemplateStatEvent,
  VcardViewPayload,
  ViewKind,
  ViewPayload,
} from './contract';

const LOAD_TIMEOUT_MS = 10_000;

function absolute(path: string): string {
  return `${backendUrl}${path}`;
}

const defaultImporter = (url: string) => import(/* @vite-ignore */ url);

interface TemplateLoaderProps {
  payload: ViewPayload;
  /**
   * Welche Ansicht gerendert werden soll (Kontrakt v3, T-29). `vcard` bleibt
   * der Default, damit bestehende Aufrufer (der eigentliche Viewer) ohne
   * Anpassung weiterlaufen — 404/410 setzen `notFound`/`gone` explizit.
   */
  view?: ViewKind;
  /**
   * Für `onEvent` (Kontrakt v3): der urlKey, unter dem eine Kontakt-
   * Betätigung gemeldet wird. Optional, damit bestehende Aufrufer/Tests ohne
   * urlKey weiter funktionieren — ohne urlKey meldet `reportStatEvent`
   * schlicht nichts (kein Aufruf ins Leere).
   */
  urlKey?: string;
  importer?: (url: string) => Promise<unknown>;
}

/** Permissiv gehalten: die tatsächliche Prop-Form (TemplateProps | ErrorProps |
 *  GoneProps | LandingProps) hängt vom `view` ab, siehe `buildProps`. */
type AnyViewComponent = (props: Record<string, unknown>) => ReactElement | null;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'classic' }
  | { kind: 'custom'; Component: AnyViewComponent };

/** Welchen Export aus dem Marken-Bundle diese Ansicht erwartet. */
function pickExport(mod: unknown, view: ViewKind): unknown {
  const record = mod as Record<string, unknown> | null | undefined;
  if (!record) return undefined;
  switch (view) {
    case 'notFound':
      return record.ErrorView;
    case 'gone':
      return record.GoneView;
    case 'landing':
      return record.LandingView;
    case 'vcard':
    default:
      return record.default;
  }
}

/** Props, die die (Marken- oder Vorgabe-)Komponente je Ansicht bekommt. */
function buildProps(
  view: ViewKind,
  payload: ViewPayload,
  asset: (name: string) => string,
  onEvent: (event: TemplateStatEvent) => void,
): Record<string, unknown> {
  if (view === 'notFound') {
    const { urlKey } = payload as NotFoundPayload;
    return { data: payload.context, asset, error: { kind: 'notFound' as const, urlKey } };
  }
  if (view === 'gone' || view === 'landing') {
    return { data: payload.context, asset };
  }
  return { data: payload.context, asset, onEvent };
}

/** Die deutsche Vorgabeseite je Ansicht — nie eine weiße Seite, siehe CLAUDE.md
 *  „Der Fallback ist der eigentliche Härtefall". */
function classicFallback(view: ViewKind, payload: ViewPayload, onEvent: (event: TemplateStatEvent) => void) {
  if (view === 'notFound') {
    const { urlKey } = payload as NotFoundPayload;
    return <ClassicNotFound data={payload.context} asset={() => ''} error={{ kind: 'notFound', urlKey }} />;
  }
  if (view === 'gone') {
    return <ClassicGone data={payload.context} asset={() => ''} />;
  }
  if (view === 'landing') {
    return <ClassicLanding data={payload.context} asset={() => ''} />;
  }
  // Hier ist view === 'vcard' (letzter verbleibender ViewKind) — payload
  // bleibt aus TS-Sicht die volle ViewPayload-Union, da `view` und
  // `payload.kind` nicht typseitig verknüpft sind, in der Praxis aber immer
  // zusammenpassen (EmployeeData setzt beide gemeinsam).
  const vcardPayload = payload as VcardViewPayload;
  return <ClassicTemplate data={vcardPayload.context} asset={() => ''} onEvent={onEvent} />;
}

export function TemplateLoader({
  payload,
  view = 'vcard',
  urlKey = '',
  importer = defaultImporter,
}: TemplateLoaderProps) {
  const tpl = payload.template;
  const onEvent = useCallback(
    (event: TemplateStatEvent) => reportStatEvent(urlKey, event),
    [urlKey],
  );
  const usable = !!tpl && SUPPORTED_CONTRACT_VERSIONS.includes(tpl.contractVersion);
  const [state, setState] = useState<LoadState>(usable ? { kind: 'loading' } : { kind: 'classic' });

  useEffect(() => {
    if (!usable || !tpl) {
      setState({ kind: 'classic' });
      return;
    }
    setState({ kind: 'loading' });
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    let styleLink: HTMLLinkElement | null = null;
    if (tpl.stylesUrl) {
      styleLink = document.createElement('link');
      styleLink.rel = 'stylesheet';
      styleLink.href = absolute(tpl.stylesUrl);
      document.head.appendChild(styleLink);
    }

    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Template-Load-Timeout')), LOAD_TIMEOUT_MS);
    });
    Promise.race([importer(absolute(tpl.bundleUrl)), timeout])
      .then((mod) => {
        if (cancelled) return;
        const Component = pickExport(mod, view);
        if (typeof Component === 'function') {
          setState({ kind: 'custom', Component: Component as AnyViewComponent });
        } else {
          setState({ kind: 'classic' });
        }
      })
      .catch((error) => {
        console.error('Template-Bundle konnte nicht geladen werden, falle auf classic zurück:', error);
        if (!cancelled) setState({ kind: 'classic' });
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      styleLink?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl?.id, usable, view]);

  const asset = useMemo(() => {
    if (!tpl) return () => '';
    const suffix = tpl.preview ? `?preview=${encodeURIComponent(tpl.preview)}` : '';
    return (name: string) => {
      const full = name.startsWith('assets/') ? name : 'assets/' + name;
      return `${absolute(tpl.assetBaseUrl)}${encodeURIComponent(full)}${suffix}`;
    };
  }, [tpl]);

  if (state.kind === 'loading') return <Loading />;

  const fallback = classicFallback(view, payload, onEvent);

  if (state.kind === 'classic') return fallback;

  const { Component } = state;
  return (
    <TemplateErrorBoundary fallback={fallback}>
      <Component {...buildProps(view, payload, asset, onEvent)} />
    </TemplateErrorBoundary>
  );
}
